import { Modal as BootstrapModal, Spinner } from 'react-bootstrap';
import { Typography } from 'app/components/ui';
import { i18n } from 'app/utils/i18n';
import {
  AvailableDimension,
  AvailableHypercube,
  AvailableMember,
  UUID,
  useDimensionsForConcept,
  useCreateTypedMemberValue,
  useDeleteExtension,
} from 'app/api/tag-queries';
import { useQueryClient } from 'react-query';
import { taxonomyQueryKeys } from 'app/api/tag-queries/taxonomy';
import { useContext, useEffect, useState } from 'react';
import { useEditorSelector } from 'app/state/contexts/EditorContext';
import { TagContext } from 'app/state/contexts/TagContext';
import { DimensionTree } from './DimensionTree';
import { ExtensionMemberModal } from './ExtensionMemberModal';
import { DeleteExtensionMemberModal } from './DeleteExtensionMemberModal';

import './Modal.scss';

interface DimensionsModalProps {
  conceptName: string;
  taxonomyId?: UUID;
  show: boolean;
  onConfirm: (dimension: AvailableDimension, member: AvailableMember, hypercube: AvailableHypercube) => void;
  onCancel: () => void;
  onTypedValueAdded?: (args: { hypercube: AvailableHypercube; dimension: AvailableDimension; typedValueId: UUID; value: string }) => void;
}

export const DimensionsModal = ({ show, conceptName, taxonomyId, onConfirm, onCancel, onTypedValueAdded }: DimensionsModalProps) => {
  const { superId: documentSuperId } = useEditorSelector((editor: any) => editor?.pDocument);
  const { tagLanguage } = useContext(TagContext);
  const { data, isLoading, isError } = useDimensionsForConcept(documentSuperId, conceptName);
  const createTypedMemberValue = useCreateTypedMemberValue();
  const deleteExtension = useDeleteExtension();
  const queryClient = useQueryClient();

  const [selection, setSelection] = useState<{
    hypercube: AvailableHypercube;
    dimension: AvailableDimension;
    member: AvailableMember;
  } | null>(null);
  const [extending, setExtending] = useState<{ axis: AvailableDimension; hypercube: AvailableHypercube; editMember?: AvailableMember } | null>(null);
  const [deleting, setDeleting] = useState<AvailableMember | null>(null);

  useEffect(() => {
    if (show) {
      setSelection(null);
      setExtending(null);
      setDeleting(null);
    }
  }, [show]);

  const hypercubes = data?.hypercubes ?? [];

  const handleConfirm = () => {
    if (!selection) return;
    onConfirm(selection.dimension, selection.member, selection.hypercube);
  };

  const handleAddMember = (axis: AvailableDimension, hypercube: AvailableHypercube) => {
    setExtending({ axis, hypercube });
  };

  const handleEditMember = (member: AvailableMember, axis: AvailableDimension, hypercube: AvailableHypercube) => {
    setExtending({ axis, hypercube, editMember: member });
  };

  const handleDeleteMember = (member: AvailableMember) => {
    if (!member.conceptId) return;
    setDeleting(member);
  };

  const handleConfirmDelete = () => {
    if (!deleting?.conceptId || !documentSuperId || !taxonomyId) return;
    deleteExtension.mutate({ extensionId: deleting.conceptId, documentSuperId, taxonomyId }, { onSettled: () => setDeleting(null) });
  };

  const memberLabel = (member: AvailableMember): string => {
    const flat = member.labels as unknown as Record<string, string | undefined>;
    return flat?.[tagLanguage] ?? flat?.en ?? flat?.fallback ?? member.id;
  };

  const handleAddTypedValue = async ({ hypercube, dimension, value }: { hypercube: AvailableHypercube; dimension: AvailableDimension; value: string }) => {
    if (!documentSuperId || !dimension.conceptId) return;
    const saved = await createTypedMemberValue.mutateAsync({
      documentSuperId,
      axisConceptId: dimension.conceptId,
      value,
    });
    queryClient.invalidateQueries([taxonomyQueryKeys.dimensionsForConcept]);
    onTypedValueAdded?.({ hypercube, dimension, typedValueId: saved.id, value: saved.value });
  };

  return (
    <>
      <BootstrapModal show={show && !extending && !deleting} onHide={onCancel} contentClassName="tags-modal__content tags-modal__content--dimensions" centered>
        <button onClick={onCancel} type="button" aria-label="Close" className="tags-modal__close-btn">
          <i className="fa-solid fa-xmark" />
        </button>

        <div className="tags-modal__header">
          <Typography variant="body-7" fontWeight="semibold" color="#1e2533">
            {i18n('tag.dimensions.add')}
          </Typography>
        </div>

        <section className="px-3" style={{ flex: 1, overflowY: 'auto' }}>
          {isLoading && (
            <div className="d-flex justify-content-center py-4">
              <Spinner />
            </div>
          )}

          {isError && (
            <div className="px-3 py-4">
              <Typography>{i18n('taxonomy.load.error')}</Typography>
            </div>
          )}

          {!isLoading && !isError && hypercubes.length === 0 && (
            <div className="px-3 py-4">
              <Typography>{i18n('tag.dimensions.no-dimensions-for-concept')}</Typography>
            </div>
          )}

          {!isLoading && !isError && hypercubes.length > 0 && (
            <DimensionTree
              hypercubes={hypercubes}
              selectedMemberId={selection?.member.id ?? null}
              onSelect={setSelection}
              onAddMember={handleAddMember}
              onEditMember={handleEditMember}
              onDeleteMember={handleDeleteMember}
              onAddTypedValue={handleAddTypedValue}
            />
          )}
        </section>

        <div className="tags-modal__dimension-footer">
          <button className="btn btn-secondary tags-modal__btn-cancel" onClick={onCancel}>
            {i18n('common.cancel')}
          </button>

          <button className="btn btn-primary tags-modal__btn-confirm" onClick={handleConfirm} disabled={!selection}>
            {i18n('common.add')}
          </button>
        </div>
      </BootstrapModal>

      {extending && taxonomyId && documentSuperId && (
        <ExtensionMemberModal
          show
          onClose={() => setExtending(null)}
          hypercube={extending.hypercube}
          axis={extending.axis}
          documentSuperId={documentSuperId}
          taxonomyId={taxonomyId}
          editMember={extending.editMember}
        />
      )}

      {deleting && (
        <DeleteExtensionMemberModal
          show
          memberLabel={memberLabel(deleting)}
          busy={deleteExtension.isLoading}
          onConfirm={handleConfirmDelete}
          onCancel={() => setDeleting(null)}
        />
      )}
    </>
  );
};
